// @ts-ignore
import { useState, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import CharitySidebar from './CharitySidebar';
import Header from './Header';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
import { useAppTheme } from '@/context/ThemeContext';
import { useLayout } from '@/context/LayoutContext';
import { createClient } from '@/services/supabase';
import type { Session } from '@supabase/supabase-js';
import type { AuthChangeEvent } from '@supabase/supabase-js';
type LayoutProps = {
  children: ReactNode;
  title: string;
  forceCharitySidebar?: boolean;
};

export default function Layout({ children, title, forceCharitySidebar = false }: LayoutProps) {
  const { user } = useAuthV2(); // Needed for auth context and displaying user info
  const location = useLocation();
  const { theme, themeConfig, setTheme } = useAppTheme();
  const { hasLayout, setHasLayout } = useLayout();
  const [isMinimized, setIsMinimized] = useState(localStorage.getItem('sidebarMinimized') === 'true');
  
  // Set hasLayout to true when this component mounts
  useEffect(() => {
    // If we're not already in a layout, set hasLayout to true
    if (!hasLayout) {
      setHasLayout(true);
    }
    
    // Clean up by setting hasLayout to false when unmounting
    return () => {
      setHasLayout(false);
    };
  }, []);
  
  // Auth state listener with JWT persistence
  useEffect(() => {
    const initAuth = async () => {
      // Create Supabase client
      const supabase = await createClient();
      
      // Subscribe to the centralized auth state
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
        if (event === 'SIGNED_OUT') {
          localStorage.removeItem('supabase.auth.token');
          localStorage.removeItem('session');
        } else if (session) {
          // Store JWT separately for persistence
          localStorage.setItem('supabase.auth.token', session.access_token);
          localStorage.setItem('session', JSON.stringify(session));
        }
      });

      // Check for existing session on mount
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        localStorage.setItem('supabase.auth.token', session.access_token);
      }
      
      return subscription;
    };
    
    // Execute the async function
    const subscriptionPromise = initAuth();
    
    // Cleanup function
    return () => {
      subscriptionPromise.then(subscription => {
        subscription.unsubscribe();
      }).catch(err => {
        console.error('Error unsubscribing from auth listener:', err);
      });
    };
  }, []);

  // Remove mobile-specific states and queries
  const SIDEBAR_EXPANDED_WIDTH = 240;
  const SIDEBAR_MINIMIZED_WIDTH = 64;
  
  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarMinimized', isMinimized.toString());
  }, [isMinimized]);
  
  const toggleSidebar = () => {
    setIsMinimized(!isMinimized);
  };
  
  const toggleTheme = () => {
    const newTheme = theme === 'charity' ? 'business' : 'charity';
    setTheme(newTheme);
  };
  
  // Simplified sidebar width calculation
  const sidebarWidth = isMinimized ? SIDEBAR_MINIMIZED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  // If we're already inside a Layout, just render the children
  if (hasLayout) {
    return <>{children}</>;
  }
  function isCharity(): boolean {
    throw new Error('Function not implemented.');
  }

  return (
    <div className="flex flex-col h-full w-full overflow-hidden min-w-[1024px]" style={{ backgroundColor: themeConfig.surface }}>
      <div className="flex flex-1 w-full overflow-hidden">
        {/* Render either Charity or Business Sidebar based on user role or explicit prop */}
        {(user?.role === 'charity' || isCharity() || location.pathname.includes('/charity/') || forceCharitySidebar) ? (
          <CharitySidebar
            isOpen={true} 
            isMinimized={isMinimized} 
            onClose={() => {}} 
            theme={theme}
          />
        ) : (
          <Sidebar 
            isOpen={true} 
            isMinimized={isMinimized} 
            onClose={() => {}} 
            theme={theme}
          />
        )}
        
        {/* Main Content */}
        <div className="flex flex-col flex-1 w-full h-full transition-all duration-300 ease-in-out">
          <Header 
            title={title} 
            onMenuClick={toggleSidebar}
            onThemeToggle={toggleTheme}
            isMinimized={isMinimized}
          />
          
          {/* Main Content Area */}
          <main 
            className="flex-1 w-full h-full overflow-auto" 
            style={{ 
              backgroundColor: themeConfig.surface, 
            }}
          >
            <div 
              className="w-full py-4 mx-auto"
              style={{ 
                paddingLeft: `calc(${sidebarWidth}px + 20px)`, 
                paddingRight: '20px' 
              }}
            >
              
              {/* Page Content */}
              <div className="pb-4">
                {children}
              </div>
            </div>
          </main>
          
          {/* Footer */}
          <footer className="w-full bg-white shadow-md py-3 px-4 text-center text-sm text-gray-600">
            <p>© {new Date().getFullYear()} Catch of the Day. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </div>
  );
}
